import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SelectControl from "../select/SelectControl";
import SelectControl_m from "../select/SelectControl_m";
import addHeaders from "../../utils/addHeader";
import IconButton from "../generic/IconButton";
import { Icon_Edit } from "../../styles/icons";

// Setup Use of API
import useApi from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query'
import { qk } from "../../utils/queryKeys";

import { useNavigate } from "react-router-dom";

// Profile & locale
import { useSnapshot } from 'valtio';
import useProfile from "../../hooks/useProfile";
import useLocale from "../../hooks/useLocale";

import generateSetValueObject from "../select/generateSetValueObject";
import SkeletonLoader from "../generic/SkeletonLoader";

const CurrencyContainer = styled.div`
    width: 100%;
    margin: 0;
    position: relative;
`

const RowWrapper = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
`

// Currency takes an entity like 'currency', a value like {name:'Euro',id:'EUR'}, a setter, and an options object.
const Currency = React.memo(({ entity, name, valueName, valueId, valueMap, setValue, options, isRed, testId, parameter }) => {

    const { lf } = useLocale();
    if (!options) { options = {} }

    // #1 — guard against null/undefined parameter
    const date = parameter
        ? parameter.split('T')[0]
        : new Date().toISOString().split('T')[0];

    const [exchangeRate, setExchangeRate] = useState(null);

    const { getMe } = useApi();
    const navigate = useNavigate();

    const { pState } = useProfile()
    const profile = useSnapshot(pState)
    const orgCountry = profile.currentOrgCountry
    const baseCurrency = profile.currentOrgCurrency || "DKK";

    const placeholder = `${entity}.placeholder`
    const modalTitle = `${entity}.modal.title`

    // #5 — guard options.list before navigating; #7 — removed console.log
    const openList = () => {
        if (options.list) navigate(options.list)
    }

    // #2 — removed try/catch so errors propagate to React Query and result.isError works
    const getData = async (key) => {
        const orgCountry = key.queryKey[2]
        const resource = `${entity}/?country=${orgCountry}`
        const response = await getMe(resource)

        let defaultValue = response?.meta?.default || ''
        if (!defaultValue) defaultValue = response.data[0]

        const list = addHeaders(response.data, options.withHeaders)
        return { list, defaultValue }
    }

    // #10 — message and showAdditionalInfo computed with useMemo
    // #15 — inverted rate uses 4dp instead of 6dp
    const message = useMemo(() => {
        if (!exchangeRate || !valueId || valueId === baseCurrency) return '';
        const formattedRate = lf(exchangeRate, 6, 6);
        const formattedInvertedRate = lf(1 / exchangeRate, 4, 4);
        return `1 ${baseCurrency} = ${formattedRate} ${valueId} | 1 ${valueId} = ${formattedInvertedRate} ${baseCurrency}`;
    }, [exchangeRate, valueId, baseCurrency, lf]);

    const showAdditionalInfo = !!(valueId && exchangeRate && valueId !== baseCurrency);

    // #3 — track the last fetched currency so the useEffect below can skip when
    //       returnData already fetched the rate for the newly selected currency.
    const lastFetchedForRef = useRef(null);

    // #8 — exchangeDate state removed (was never rendered)
    // #12 — fetchExchangeRate wrapped in useCallback for stable reference
    // #14 — when currency equals base, exchangeDate was being set to doc date (misleading); removed with #8
    const fetchExchangeRate = useCallback(async (currencyCode) => {
        if (!currencyCode || currencyCode === baseCurrency) {
            setExchangeRate(1);
            lastFetchedForRef.current = currencyCode;
            return 1;
        }
        try {
            const resource = `fxRate/${date}/${currencyCode}`;
            const response = await getMe(resource);
            if (response?.exchangeRate) {
                setExchangeRate(response.exchangeRate ?? 1);
                lastFetchedForRef.current = currencyCode;
                return response.exchangeRate;
            }
        } catch (error) {
            console.error("Error fetching exchange rate:", error);
        }
    }, [baseCurrency, date, getMe]);

    // #4 — use spread to avoid mutating the selection object from the list
    const returnData = async (data, valueMap) => {
        const rate = await fetchExchangeRate(data.id) ?? 1;
        const enriched = { ...data, baseCurrency, exchangeRate: rate };
        setValue(generateSetValueObject(enriched, valueMap));
    }

    // #9 — staleTime added; currency lists change rarely
    const result = useQuery({
        queryKey: qk.currency(entity, orgCountry),
        queryFn: getData,
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60,
    })

    // Auto-fill default value when the field arrives empty
    // #4 — spread to avoid mutating cached defaultValue
    // #13 — tightened deps to [result.isSuccess, valueName]; result.data is read
    //        safely inside the guard, entity/valueMap/setValue are stable per mount
    useEffect(() => {
        if (result.isSuccess && valueName === '' && entity && result.data?.defaultValue) {
            const enriched = { ...result.data.defaultValue, exchangeRate: 1, baseCurrency };
            setValue(generateSetValueObject(enriched, valueMap));
            if (result.data.defaultValue.id) {
                fetchExchangeRate(result.data.defaultValue.id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result.isSuccess, valueName]);

    // Fetch exchange rate for display when valueId changes externally (e.g. initial load,
    // form hydration). Skipped when returnData already fetched for this currency (#3).
    useEffect(() => {
        if (valueId && lastFetchedForRef.current !== valueId) {
            fetchExchangeRate(valueId);
        }
    }, [valueId, fetchExchangeRate]);

    if (result.isLoading) {
        return <SkeletonLoader config={['F']} compact />
    }

    if (result.isError) {
        return <span>Error: {result.error ? result.error.message : 'Internet Issues'}</span>
    }

    if (result.isSuccess) {
        return (
            <>
                <RowWrapper name="CurrencyWrapper">
                    <CurrencyContainer name='CurrencyContainer'>
                        <SelectControl
                            name={name}
                            valueName={valueName}
                            valueId={valueId}
                            setValue={setValue}
                            data={result.data.list}
                            defaultValue={result.data.defaultValue}
                            placeholder={placeholder}
                            onSelect={selection => returnData(selection, valueMap)}
                            entity={entity}
                            options={options}
                            isRed={isRed}
                            testId={testId}
                            additionalInfo={showAdditionalInfo ? message : null}
                        />
                        <SelectControl_m
                            name={name}
                            valueName={valueName}
                            valueId={valueId}
                            setValue={setValue}
                            data={result.data.list}
                            defaultValue={result.data.defaultValue}
                            placeholder={placeholder}
                            onSelect={selection => returnData(selection, valueMap)}
                            entity={entity}
                            modalTitle={modalTitle}
                            options={options}
                            isRed={isRed}
                            testId={testId}
                            additionalInfo={showAdditionalInfo ? message : null}
                        />
                    </CurrencyContainer>
                    {options.edit && <IconButton clickAction={openList} icon={Icon_Edit} />}
                </RowWrapper>
            </>
        )
    }
});

export default Currency;
